'use client';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import Container from '@/components/container';

export default function CommentForm(props) {
  const { comments: initialComments, id } = props;
  const [comments] = useState(initialComments);
  const [submitted, setSubmitted] = useState(false);
  const [rating, setRating] = useState(0);
  const [isHovering, setIsHovering] = useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async data => {
    data.rating = rating; // Add the rating to the data
    try {
      await fetch('/api/createComment', {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setSubmitted(false);
    }
  };

  // Function to format date in a more readable way
  const formatDate = dateString => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 1) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      return `${Math.floor(diffDays / 7)} weeks ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
  };

  return (
    <Container>
      <div className="mx-auto max-w-screen-lg">
        <div className="my-1 border-t border-gray-200 pt-10 dark:border-gray-800"></div>

        {submitted ? (
          <div className="mb-12 overflow-hidden rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 p-8 shadow-sm dark:from-green-900/30 dark:to-emerald-900/30">
            <div className="flex items-center">
              <div className="mr-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-800">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-green-600 dark:text-green-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Thank you for your comment!
                </h3>
                <p className="mt-1 text-gray-600 dark:text-gray-300">
                  It will appear below once approved by our team.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-12 overflow-hidden rounded-lg bg-white shadow-sm dark:bg-gray-900">
            <div className="border-b border-gray-100 bg-gray-50 px-6 py-4 dark:bg-gray-900">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Leave a comment</h3>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-6">
              <input {...register('_id')} type="hidden" name="_id" value={id} />

              <div className="mb-6 grid gap-6 md:grid-cols-2">
                <div>
                  <label
                    htmlFor="name"
                    className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Name
                  </label>
                  <input
                    {...register('name', { required: true })}
                    id="name"
                    className="focus:ring-opacity-50 w-full rounded-md border border-gray-200 bg-white px-4 py-2.5 text-gray-900 transition duration-150 focus:border-blue-500 focus:ring focus:ring-blue-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400 dark:focus:ring-blue-400/25"
                    placeholder="Your name"
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-500">Name is required</p>}
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Email
                  </label>
                  <input
                    {...register('email', {
                      required: true,
                      pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    })}
                    id="email"
                    type="email"
                    className="focus:ring-opacity-50 w-full rounded-md border border-gray-200 bg-white px-4 py-2.5 text-gray-900 transition duration-150 focus:border-blue-500 focus:ring focus:ring-blue-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400 dark:focus:ring-blue-400/25"
                    placeholder="Your email"
                  />
                  {errors.email?.type === 'required' && (
                    <p className="mt-1 text-xs text-red-500">Email is required</p>
                  )}
                  {errors.email?.type === 'pattern' && (
                    <p className="mt-1 text-xs text-red-500">Please enter a valid email</p>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <label
                  htmlFor="comment"
                  className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Your comment
                </label>
                <textarea
                  {...register('comment', { required: true })}
                  id="comment"
                  rows="4"
                  className="focus:ring-opacity-50 w-full rounded-md border border-gray-200 bg-white px-4 py-2.5 text-gray-900 transition duration-150 focus:border-blue-500 focus:ring focus:ring-blue-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400 dark:focus:ring-blue-400/25"
                  placeholder="Share your thoughts..."
                ></textarea>
                {errors.comment && <p className="mt-1 text-xs text-red-500">Comment is required</p>}
              </div>

              <div className="mb-6">
                <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Rating
                </span>
                <div className="flex items-center">
                  {[1, 2, 3, 4, 5].map(value => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setRating(value)}
                      onMouseEnter={() => setIsHovering(value)}
                      onMouseLeave={() => setIsHovering(0)}
                      className="mr-1 rounded-full p-1 transition duration-100 hover:bg-gray-100 focus:outline-none dark:hover:bg-gray-700"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-6 w-6 ${
                          value <= (isHovering || rating)
                            ? 'text-yellow-400'
                            : 'text-gray-300 dark:text-gray-500'
                        } ${value <= isHovering && value > rating ? 'animate-pulse' : ''}`}
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  ))}
                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                    {rating > 0 ? `${rating}/5` : 'Select rating'}
                  </span>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="rounded-md bg-gradient-to-r from-green-600 to-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition duration-150 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none dark:from-purple-600 dark:to-blue-600 dark:focus:ring-offset-gray-800"
                >
                  Post Comment
                </button>
              </div>
            </form>
          </div>
        )}

        {comments?.length > 0 && (
          <div className="mb-10">
            <h3 className="mb-6 text-2xl font-semibold text-gray-900 dark:text-white">
              Comments{' '}
              <span className="ml-2 rounded-full bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                {comments.length}
              </span>
            </h3>

            <div className="space-y-6">
              {comments.map(comment => (
                <div
                  key={comment._id}
                  className="overflow-hidden rounded-lg border border-gray-100 bg-white p-6 shadow-sm transition duration-200 hover:shadow-md dark:border-gray-700 dark:bg-gray-800"
                >
                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 text-lg font-semibold text-white">
                        {comment.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="ml-3">
                        <h4 className="text-base font-medium text-gray-900 dark:text-white">
                          {comment.name}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(comment._createdAt)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      {[1, 2, 3, 4, 5].map(value => (
                        <svg
                          key={value}
                          xmlns="http://www.w3.org/2000/svg"
                          className={`h-4 w-4 ${
                            value <= comment.rating
                              ? 'text-yellow-400'
                              : 'text-gray-300 dark:text-gray-600'
                          }`}
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-md bg-gray-50 p-4 dark:bg-gray-700/30">
                    <p className="text-gray-700 dark:text-gray-300">{comment.comment}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Container>
  );
}
